import { useState, useMemo, useEffect } from "react";
import { 
  X, Calendar as CalendarIcon, 
  ChevronLeft, ChevronRight, Award,
  Clock, ArrowUpRight, ArrowDownRight, Wallet,
  Flame, BarChart3, ExternalLink, Search
} from "lucide-react";
import { formatUsdCompact } from "@/lib/formatUsd";
import { TokenAvatar } from "@/components/TokenAvatar";
import { DayTradesDetailModal, type DayDataPayload } from "@/components/DayTradesDetailModal";

interface TraderCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  trades: any[];
  solPrice?: number;
  initialDepositSol?: number;
  onSelectMint?: (mint: string) => void;
  backendStats?: any;
}

function formatDuration(ms: number) {
  if (!ms || ms < 0) return "0s";
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainingSecs = secs % 60;
  if (mins < 60) return `${mins}m ${remainingSecs}s`;
  const hrs = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  if (hrs < 24) return `${hrs}h ${remainingMins}m`;
  const days = Math.floor(hrs / 24);
  const remainingHrs = hrs % 24;
  return `${days}d ${remainingHrs}h`;
}

function truncate(str: string, max = 8) {
  if (!str) return "";
  if (str.length <= max) return str;
  return str.substring(0, 4) + "..." + str.substring(str.length - 4);
}

function parseTimestamp(raw: any): number {
  if (!raw) return 0;
  if (typeof raw === "number") {
    return raw < 1e11 ? raw * 1000 : raw;
  }
  if (typeof raw === "string") {
    const num = Number(raw);
    if (!isNaN(num) && num > 0) {
      return num < 1e11 ? num * 1000 : num;
    }
    const parsed = Date.parse(raw);
    if (!isNaN(parsed) && parsed > 0) return parsed;
  }
  if (raw instanceof Date) {
    const t = raw.getTime();
    if (!isNaN(t) && t > 0) return t;
  }
  return 0;
}

export function TraderCalendarModal({
  isOpen,
  onClose,
  trades = [],
  solPrice = 150,
  initialDepositSol = 4.13,
  onSelectMint,
  backendStats,
}: TraderCalendarModalProps) {
  const [currencyMode, setCurrencyMode] = useState<"SOL" | "USD">("SOL");
  const [filterMode, setFilterMode] = useState<"ALL" | "WINS" | "LOSSES">("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [spreadMode, setSpreadMode] = useState(false);

  // Handle ESC key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Clean closed trades list with robust timestamp parsing
  const closedTrades = useMemo(() => {
    return (trades || [])
      .filter(t => t.action === "sell" || t.profitSol != null || t.pnlPct != null)
      .map(t => {
        const profitSol = t.profitSol != null 
          ? Number(t.profitSol) 
          : ((Number(t.pnlUsd) || 0) / (solPrice || 150));
        const pnlUsd = t.pnlUsd != null 
          ? Number(t.pnlUsd) 
          : (profitSol * (solPrice || 150));
        const pnlPct = Number(t.pnlPct) || 0;

        let ts = parseTimestamp(t.timestamp) || 
                 parseTimestamp(t.exitTs) || 
                 parseTimestamp(t.closeTime) || 
                 parseTimestamp(t.createdAt);
                 
        if (!ts && typeof t.id === "string") {
          const idMatch = t.id.match(/\d{10,13}/);
          if (idMatch) ts = parseTimestamp(idMatch[0]);
        }
        if (!ts) ts = parseTimestamp(t.entryTime) || Date.now();

        const entryTs = parseTimestamp(t.entryTime) || (ts - 60000);
        return {
          ...t,
          profitSol,
          pnlUsd,
          pnlPct,
          timestamp: ts,
          entryTime: entryTs,
          duration: Math.max(0, ts - entryTs),
        };
      })
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [trades, solPrice]);

  // Detect if majority of trades are clustered on a single date (e.g. today's live session)
  const isClusteredOnSingleDay = useMemo(() => {
    if (closedTrades.length < 10) return false;
    const dateCounts = new Map<string, number>();
    closedTrades.forEach(t => {
      const d = new Date(t.timestamp);
      const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      dateCounts.set(k, (dateCounts.get(k) || 0) + 1);
    });
    const maxCount = Math.max(...Array.from(dateCounts.values()));
    return maxCount / closedTrades.length > 0.85;
  }, [closedTrades]);

  // Effective trades: either exact real session timestamps or distributed across the month
  const effectiveTrades = useMemo(() => {
    if (!spreadMode || closedTrades.length === 0) return closedTrades;

    const now = new Date();
    const currentDay = Math.max(1, now.getDate());
    const year = now.getFullYear();
    const month = now.getMonth();

    return closedTrades.map((t, index) => {
      const targetDay = 1 + (index % currentDay);
      const origDate = new Date(t.timestamp);
      const newDate = new Date(year, month, targetDay, origDate.getHours(), origDate.getMinutes(), origDate.getSeconds());
      const newTs = newDate.getTime();
      const diff = newTs - t.timestamp;
      return {
        ...t,
        timestamp: newTs,
        entryTime: t.entryTime ? t.entryTime + diff : newTs - (t.duration || 60000),
      };
    }).sort((a, b) => b.timestamp - a.timestamp);
  }, [closedTrades, spreadMode]);

  // Derive initial deposit date
  const earliestTradeTs = useMemo(() => {
    if (effectiveTrades.length === 0) return Date.now();
    return Math.min(...effectiveTrades.map(t => t.entryTime || t.timestamp));
  }, [effectiveTrades]);

  // Selected calendar month/year & Day detail modal state
  const [viewDate, setViewDate] = useState<Date>(() => new Date());
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [activeDayModalData, setActiveDayModalData] = useState<DayDataPayload | null>(null);

  // Initialize view date to most recent trade date
  useEffect(() => {
    if (effectiveTrades.length > 0) {
      const latest = new Date(effectiveTrades[0].timestamp);
      setViewDate(new Date(latest.getFullYear(), latest.getMonth(), 1));
      const dayKey = `${latest.getFullYear()}-${String(latest.getMonth() + 1).padStart(2, "0")}-${String(latest.getDate()).padStart(2, "0")}`;
      setSelectedDayKey(dayKey);
    }
  }, [effectiveTrades]);

  // Advanced KPIs (Backend accelerated when available)
  const kpis = useMemo(() => {
    if (backendStats?.kpis && !spreadMode) {
      const bk = backendStats.kpis;
      return {
        totalTrades: bk.totalTrades || 0,
        winCount: bk.winCount || 0,
        lossCount: bk.lossCount || 0,
        winRate: bk.winRate || 0,
        netProfitSol: bk.netProfitSol || 0,
        netProfitUsd: bk.netProfitUsd || 0,
        grossProfitSol: bk.grossProfitSol || 0,
        grossLossSol: bk.grossLossSol || 0,
        profitFactor: bk.profitFactor || 0,
        avgWinSol: bk.avgWinSol || 0,
        avgLossSol: bk.avgLossSol || 0,
        winLossRatio: bk.winLossRatio || 0,
        expectancySol: bk.expectancySol || 0,
        sharpeRatio: bk.sharpeRatio || "0.00",
        sortinoRatio: bk.sortinoRatio || 0,
        maxDrawdownSol: bk.maxDrawdownSol || 0,
        maxDrawdownPct: bk.maxDrawdownPct || 0,
        avgHoldDuration: bk.avgHoldDuration || 0,
        currentStreak: bk.currentStreak || { type: "NONE", count: 0 },
        maxWinStreak: bk.maxWinStreak || 0,
        maxLossStreak: bk.maxLossStreak || 0,
        bestTrade: bk.bestTrade || null,
        worstTrade: bk.worstTrade || null,
        bestDay: bk.bestDay || null,
        worstDay: bk.worstDay || null,
        totalDepositsSol: initialDepositSol,
        roiOnDepositPct: initialDepositSol > 0 ? ((bk.netProfitSol || 0) / initialDepositSol) * 100 : 0,
        strategyPnL: (bk.strategyPnL || {}) as Record<string, { pnlSol: number; count: number; wins: number }>,
      };
    }

    const totalTrades = effectiveTrades.length;
    if (totalTrades === 0) {
      return {
        totalTrades: 0,
        winCount: 0,
        lossCount: 0,
        winRate: 0,
        netProfitSol: 0,
        netProfitUsd: 0,
        grossProfitSol: 0,
        grossLossSol: 0,
        profitFactor: 0,
        avgWinSol: 0,
        avgLossSol: 0,
        winLossRatio: 0,
        expectancySol: 0,
        sharpeRatio: 0,
        sortinoRatio: 0,
        maxDrawdownSol: 0,
        maxDrawdownPct: 0,
        avgHoldDuration: 0,
        currentStreak: { type: "NONE", count: 0 },
        maxWinStreak: 0,
        maxLossStreak: 0,
        bestTrade: null,
        worstTrade: null,
        bestDay: null,
        worstDay: null,
        totalDepositsSol: initialDepositSol,
        roiOnDepositPct: 0,
        strategyPnL: {} as Record<string, { pnlSol: number; count: number; wins: number }>,
      };
    }

    const wins = effectiveTrades.filter(t => t.profitSol > 0);
    const losses = effectiveTrades.filter(t => t.profitSol <= 0);
    const winRate = (wins.length / totalTrades) * 100;

    const grossProfitSol = wins.reduce((sum, t) => sum + t.profitSol, 0);
    const grossLossSol = Math.abs(losses.reduce((sum, t) => sum + t.profitSol, 0));
    const netProfitSol = grossProfitSol - grossLossSol;
    const netProfitUsd = netProfitSol * solPrice;

    const profitFactor = grossLossSol > 0 ? grossProfitSol / grossLossSol : grossProfitSol > 0 ? 99.9 : 0;
    const avgWinSol = wins.length > 0 ? grossProfitSol / wins.length : 0;
    const avgLossSol = losses.length > 0 ? grossLossSol / losses.length : 0;
    const winLossRatio = avgLossSol > 0 ? avgWinSol / avgLossSol : avgWinSol > 0 ? 99.9 : 0;
    const expectancySol = netProfitSol / totalTrades;

    // Durations
    const totalHold = effectiveTrades.reduce((sum, t) => sum + t.duration, 0);
    const avgHoldDuration = totalHold / totalTrades;

    // Streaks (chronological order)
    const chronological = [...effectiveTrades].sort((a, b) => a.timestamp - b.timestamp);
    let maxWinStreak = 0;
    let maxLossStreak = 0;
    let currentWinStreak = 0;
    let currentLossStreak = 0;

    let peakCum = 0;
    let maxDrawdownSol = 0;
    let curCum = 0;

    // Returns array for Sharpe/Sortino
    const returns: number[] = [];
    const downsideReturns: number[] = [];

    chronological.forEach(t => {
      curCum += t.profitSol;
      if (curCum > peakCum) peakCum = curCum;
      const dd = peakCum - curCum;
      if (dd > maxDrawdownSol) maxDrawdownSol = dd;

      returns.push(t.pnlPct);
      if (t.pnlPct < 0) downsideReturns.push(t.pnlPct);

      if (t.profitSol > 0) {
        currentWinStreak++;
        currentLossStreak = 0;
        if (currentWinStreak > maxWinStreak) maxWinStreak = currentWinStreak;
      } else {
        currentLossStreak++;
        currentWinStreak = 0;
        if (currentLossStreak > maxLossStreak) maxLossStreak = currentLossStreak;
      }
    });

    const currentStreak = currentWinStreak > 0
      ? { type: "WIN" as const, count: currentWinStreak }
      : currentLossStreak > 0
      ? { type: "LOSS" as const, count: currentLossStreak }
      : { type: "NONE" as const, count: 0 };

    // Sharpe Ratio
    const meanReturn = returns.reduce((a, b) => a + b, 0) / returns.length;
    const variance = returns.reduce((sum, r) => sum + Math.pow(r - meanReturn, 2), 0) / returns.length;
    const stdDev = Math.sqrt(variance);
    const sharpeRatio = stdDev > 0 ? (meanReturn / stdDev) * Math.sqrt(Math.min(returns.length, 252)) : 0;

    // Sortino Ratio
    const downsideVariance = downsideReturns.length > 0 
      ? downsideReturns.reduce((sum, r) => sum + Math.pow(r, 2), 0) / downsideReturns.length 
      : 0;
    const downsideStdDev = Math.sqrt(downsideVariance);
    const sortinoRatio = downsideStdDev > 0 ? (meanReturn / downsideStdDev) * Math.sqrt(Math.min(returns.length, 252)) : 0;

    const maxDrawdownPct = peakCum > 0 ? (maxDrawdownSol / peakCum) * 100 : 0;

    // Best and worst single trade
    const sortedByPnl = [...effectiveTrades].sort((a, b) => b.profitSol - a.profitSol);
    const bestTrade = sortedByPnl[0] || null;
    const worstTrade = sortedByPnl[sortedByPnl.length - 1] || null;

    // Group by Strategy / Trigger Reason
    const strategyPnL: Record<string, { pnlSol: number; count: number; wins: number }> = {};
    effectiveTrades.forEach(t => {
      const strat = t.alpha || t.openReason || "Momentum Scalper";
      if (!strategyPnL[strat]) strategyPnL[strat] = { pnlSol: 0, count: 0, wins: 0 };
      strategyPnL[strat].pnlSol += t.profitSol;
      strategyPnL[strat].count++;
      if (t.profitSol > 0) strategyPnL[strat].wins++;
    });

    const totalDepositsSol = initialDepositSol;
    const roiOnDepositPct = totalDepositsSol > 0 ? (netProfitSol / totalDepositsSol) * 100 : 0;

    return {
      totalTrades,
      winCount: wins.length,
      lossCount: losses.length,
      winRate,
      netProfitSol,
      netProfitUsd,
      grossProfitSol,
      grossLossSol,
      profitFactor,
      avgWinSol,
      avgLossSol,
      winLossRatio,
      expectancySol,
      sharpeRatio,
      sortinoRatio,
      maxDrawdownSol,
      maxDrawdownPct,
      avgHoldDuration,
      currentStreak,
      maxWinStreak,
      maxLossStreak,
      bestTrade,
      worstTrade,
      totalDepositsSol,
      roiOnDepositPct,
      strategyPnL,
    };
  }, [effectiveTrades, solPrice, initialDepositSol]);

  // Group trades by Calendar Day (Backend accelerated when available)
  const dailyDataMap = useMemo(() => {
    const map = new Map<string, {
      dateKey: string;
      date: Date;
      pnlSol: number;
      pnlUsd: number;
      tradesCount: number;
      winCount: number;
      lossCount: number;
      trades: any[];
      depositsSol: number;
    }>();

    if (backendStats?.dailyDataMap && !spreadMode) {
      // Use backend precomputed daily aggregates for all 3k+ trades
      for (const [dateKey, daySummary] of Object.entries(backendStats.dailyDataMap as Record<string, any>)) {
        const parts = dateKey.split("-").map(Number);
        const dateObj = new Date(parts[0], parts[1] - 1, parts[2]);

        const dayTrades = effectiveTrades.filter(t => {
          const td = new Date(t.timestamp);
          const k = `${td.getFullYear()}-${String(td.getMonth() + 1).padStart(2, "0")}-${String(td.getDate()).padStart(2, "0")}`;
          return k === dateKey;
        });

        map.set(dateKey, {
          dateKey,
          date: dateObj,
          pnlSol: daySummary.pnlSol || 0,
          pnlUsd: daySummary.pnlUsd || 0,
          tradesCount: daySummary.tradesCount || 0,
          winCount: daySummary.winCount || 0,
          lossCount: daySummary.lossCount || 0,
          trades: dayTrades,
          depositsSol: daySummary.depositsSol || 0,
        });
      }
      return map;
    }

    // Mark earliest trade day as initial deposit day
    const earliestDayKey = (() => {
      const d = new Date(earliestTradeTs);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    })();

    effectiveTrades.forEach(t => {
      const d = new Date(t.timestamp);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

      const existing = map.get(key) || {
        dateKey: key,
        date: new Date(d.getFullYear(), d.getMonth(), d.getDate()),
        pnlSol: 0,
        pnlUsd: 0,
        tradesCount: 0,
        winCount: 0,
        lossCount: 0,
        trades: [],
        depositsSol: key === earliestDayKey ? initialDepositSol : 0,
      };

      existing.pnlSol += t.profitSol;
      existing.pnlUsd += t.pnlUsd;
      existing.tradesCount++;
      if (t.profitSol > 0) existing.winCount++;
      else existing.lossCount++;
      existing.trades.push(t);

      map.set(key, existing);
    });

    return map;
  }, [effectiveTrades, earliestTradeTs, initialDepositSol, backendStats, spreadMode]);

  // Calendar matrix calculation for viewDate month
  const calendarDays = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sun, 1 = Mon ...
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Days from previous month to fill leading blank slots
    const daysFromPrevMonth = startingDayOfWeek;
    const prevMonthLastDate = new Date(year, month, 0).getDate();

    const cells: {
      dayNumber: number;
      dateKey: string;
      isCurrentMonth: boolean;
      data?: ReturnType<typeof dailyDataMap.get>;
    }[] = [];

    // Leading days
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const day = prevMonthLastDate - i;
      const prevDate = new Date(year, month - 1, day);
      const key = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      cells.push({
        dayNumber: day,
        dateKey: key,
        isCurrentMonth: false,
        data: dailyDataMap.get(key),
      });
    }

    // Days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      cells.push({
        dayNumber: day,
        dateKey: key,
        isCurrentMonth: true,
        data: dailyDataMap.get(key),
      });
    }

    // Trailing days to fill 7x5 or 7x6 grid
    const totalSlots = Math.ceil(cells.length / 7) * 7;
    const trailingCount = totalSlots - cells.length;
    for (let day = 1; day <= trailingCount; day++) {
      const nextDate = new Date(year, month + 1, day);
      const key = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      cells.push({
        dayNumber: day,
        dateKey: key,
        isCurrentMonth: false,
        data: dailyDataMap.get(key),
      });
    }

    return cells;
  }, [viewDate, dailyDataMap]);

  // Selected Day Details
  const selectedDayData = useMemo(() => {
    if (!selectedDayKey) return null;
    return dailyDataMap.get(selectedDayKey) || null;
  }, [selectedDayKey, dailyDataMap]);

  // Filtered trades for selected day with search query support
  const filteredSelectedDayTrades = useMemo(() => {
    if (!selectedDayData) return [];
    let list = selectedDayData.trades;
    if (filterMode === "WINS") list = list.filter(t => t.profitSol > 0);
    if (filterMode === "LOSSES") list = list.filter(t => t.profitSol <= 0);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(t => {
        const sym = (t.tokenMeta?.symbol || t.symbol || "").toLowerCase();
        const name = (t.tokenMeta?.name || t.name || "").toLowerCase();
        const mint = (t.mint || "").toLowerCase();
        return sym.includes(q) || name.includes(q) || mint.includes(q);
      });
    }
    return list;
  }, [selectedDayData, filterMode, searchQuery]);

  // Month navigation
  const prevMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const monthName = viewDate.toLocaleString("default", { month: "long", year: "numeric" });

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-md overflow-hidden animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-7xl max-h-[92vh] flex flex-col bg-neutral-950 border border-emerald-500/25 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glowing border accents */}
        <div className="absolute top-0 left-1/4 right-1/4 h-[1px] bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent pointer-events-none" />
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-28 bg-emerald-500/15 blur-3xl pointer-events-none rounded-full" />

        {/* ── Modal Header ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-black/40 shrink-0">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.2)]">
              <CalendarIcon className="size-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base sm:text-lg font-black tracking-tight text-white flex items-center gap-2">
                  FlyBrain Trading Journal & Performance Calendar
                </h3>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-bold">
                  {backendStats?.totalCount || closedTrades.length} Trades
                </span>
              </div>
              <p className="text-[11px] text-white/40 font-mono">
                Track simulated deposits, daily profits, and algorithmic execution over time.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Currency toggle */}
            <div className="flex items-center bg-black/50 border border-white/10 rounded-lg p-0.5 font-mono text-[10px]">
              <button
                onClick={() => setCurrencyMode("SOL")}
                className={`px-2.5 py-1 rounded font-bold transition-all ${
                  currencyMode === "SOL" 
                    ? "bg-emerald-500 text-black shadow-sm" 
                    : "text-white/60 hover:text-white"
                }`}
              >
                SOL
              </button>
              <button
                onClick={() => setCurrencyMode("USD")}
                className={`px-2.5 py-1 rounded font-bold transition-all ${
                  currencyMode === "USD" 
                    ? "bg-emerald-500 text-black shadow-sm" 
                    : "text-white/60 hover:text-white"
                }`}
              >
                USD
              </button>
            </div>

            <button
              onClick={onClose}
              className="p-1.5 rounded-lg bg-white/5 hover:bg-rose-500/20 text-white/50 hover:text-rose-400 border border-transparent hover:border-rose-500/30 transition-colors"
              title="Close modal (Esc)"
            >
              <X className="size-5" />
            </button>
          </div>
        </div>

        {/* ── Scrollable Body ─────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-5 space-y-6">

          {/* ── Top Section: Advanced KPIs ─────────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-[11px] font-black uppercase tracking-widest text-emerald-400 flex items-center gap-2">
                <BarChart3 className="size-3.5" /> Advanced Trading KPIs & Behavior Metrics
              </h4>
              <span className="text-[10px] text-white/30 font-mono">
                Active Capital Required: {kpis.totalDepositsSol.toFixed(2)} SOL
              </span>
            </div>

            {/* Core Metrics Row 1 */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2.5">
              {/* Net PnL */}
              <div className="bg-neutral-900/90 border border-emerald-500/20 rounded-xl p-3 flex flex-col justify-between relative overflow-hidden">
                <span className="text-[9px] uppercase tracking-wider text-emerald-400/70 font-bold">Total Net Profit</span>
                <div className="mt-1">
                  <span className={`text-base sm:text-lg font-mono font-black ${kpis.netProfitSol >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                    {kpis.netProfitSol >= 0 ? "+" : ""}{currencyMode === "SOL" 
                      ? `${kpis.netProfitSol.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SOL`
                      : `$${kpis.netProfitUsd.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                  </span>
                  <div className="text-[10px] font-mono text-emerald-400/80 mt-0.5 font-bold">
                    +{kpis.roiOnDepositPct.toLocaleString(undefined, { maximumFractionDigits: 0 })}% ROI
                  </div>
                </div>
              </div>

              {/* Win Rate */}
              <div className="bg-neutral-900/90 border border-white/10 rounded-xl p-3 flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-white/40 font-bold">Win Rate</span>
                <div className="mt-1">
                  <span className="text-base sm:text-lg font-mono font-black text-white">
                    {kpis.winRate.toFixed(1)}%
                  </span>
                  <div className="text-[10px] font-mono text-white/40 mt-0.5">
                    <span className="text-emerald-400 font-bold">{kpis.winCount}W</span> / <span className="text-rose-400 font-bold">{kpis.lossCount}L</span>
                  </div>
                </div>
              </div>

              {/* Profit Factor */}
              <div className="bg-neutral-900/90 border border-white/10 rounded-xl p-3 flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-white/40 font-bold">Profit Factor</span>
                <div className="mt-1">
                  <span className="text-base sm:text-lg font-mono font-black text-white">
                    {kpis.profitFactor.toFixed(2)}x
                  </span>
                  <div className="text-[10px] font-mono text-white/40 mt-0.5">
                    W/L Ratio: <span className="text-white/80 font-bold">{kpis.winLossRatio.toFixed(1)}</span>
                  </div>
                </div>
              </div>

              {/* Avg Win vs Avg Loss */}
              <div className="bg-neutral-900/90 border border-white/10 rounded-xl p-3 flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-white/40 font-bold">Avg Win / Loss</span>
                <div className="mt-1">
                  <div className="text-xs font-mono font-bold text-emerald-400">
                    +{currencyMode === "SOL" ? `${kpis.avgWinSol.toFixed(2)} SOL` : `$${(kpis.avgWinSol * solPrice).toFixed(0)}`}
                  </div>
                  <div className="text-xs font-mono font-bold text-rose-400 mt-0.5">
                    -{currencyMode === "SOL" ? `${kpis.avgLossSol.toFixed(2)} SOL` : `$${(kpis.avgLossSol * solPrice).toFixed(0)}`}
                  </div>
                </div>
              </div>

              {/* Sharpe & Sortino */}
              <div className="bg-neutral-900/90 border border-white/10 rounded-xl p-3 flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-white/40 font-bold">Risk Ratios</span>
                <div className="mt-1">
                  <div className="text-xs font-mono font-bold text-white/90">
                    Sharpe: <span className="text-cyan-400">{kpis.sharpeRatio.toFixed(2)}</span>
                  </div>
                  <div className="text-xs font-mono font-bold text-white/90 mt-0.5">
                    Sortino: <span className="text-purple-400">{kpis.sortinoRatio.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Max Drawdown */}
              <div className="bg-neutral-900/90 border border-white/10 rounded-xl p-3 flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-white/40 font-bold">Max Drawdown</span>
                <div className="mt-1">
                  <span className="text-base sm:text-lg font-mono font-black text-rose-400">
                    -{kpis.maxDrawdownSol.toFixed(2)} SOL
                  </span>
                  <div className="text-[10px] font-mono text-rose-400/60 mt-0.5">
                    -{kpis.maxDrawdownPct.toFixed(1)}% peak drop
                  </div>
                </div>
              </div>
            </div>

            {/* Behavioral Metrics Row 2 */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2.5 mt-2.5">
              {/* Avg Hold Duration */}
              <div className="bg-black/40 border border-white/5 rounded-xl p-2.5 flex items-center gap-2.5">
                <Clock className="size-4 text-blue-400 shrink-0" />
                <div>
                  <div className="text-[8px] uppercase tracking-wider text-white/40 font-bold">Avg Hold Time</div>
                  <div className="text-xs font-mono font-bold text-white/90">{formatDuration(kpis.avgHoldDuration)}</div>
                </div>
              </div>

              {/* Streaks */}
              <div className="bg-black/40 border border-white/5 rounded-xl p-2.5 flex items-center gap-2.5">
                <Flame className="size-4 text-amber-400 shrink-0" />
                <div>
                  <div className="text-[8px] uppercase tracking-wider text-white/40 font-bold">Streak Record</div>
                  <div className="text-xs font-mono font-bold text-white/90">
                    <span className="text-emerald-400">▲{kpis.maxWinStreak}W</span> max / <span className="text-rose-400">▼{kpis.maxLossStreak}L</span>
                  </div>
                </div>
              </div>

              {/* Expectancy */}
              <div className="bg-black/40 border border-white/5 rounded-xl p-2.5 flex items-center gap-2.5">
                <Award className="size-4 text-emerald-400 shrink-0" />
                <div>
                  <div className="text-[8px] uppercase tracking-wider text-white/40 font-bold">Expectancy / Trade</div>
                  <div className="text-xs font-mono font-bold text-emerald-400">
                    +{currencyMode === "SOL" ? `${kpis.expectancySol.toFixed(2)} SOL` : `$${(kpis.expectancySol * solPrice).toFixed(0)}`}
                  </div>
                </div>
              </div>

              {/* Initial Capital / Deposit */}
              <div className="bg-black/40 border border-white/5 rounded-xl p-2.5 flex items-center gap-2.5">
                <Wallet className="size-4 text-purple-400 shrink-0" />
                <div>
                  <div className="text-[8px] uppercase tracking-wider text-white/40 font-bold">Initial Deposit</div>
                  <div className="text-xs font-mono font-bold text-white/90">
                    {kpis.totalDepositsSol.toFixed(2)} SOL <span className="text-white/40">(${formatUsdCompact(kpis.totalDepositsSol * solPrice)})</span>
                  </div>
                </div>
              </div>

              {/* Best Trade */}
              <div className="bg-black/40 border border-white/5 rounded-xl p-2.5 flex items-center gap-2.5">
                <ArrowUpRight className="size-4 text-emerald-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[8px] uppercase tracking-wider text-white/40 font-bold">Best Trade</div>
                  <div className="text-xs font-mono font-bold text-emerald-400 truncate">
                    {kpis.bestTrade ? `+${kpis.bestTrade.profitSol.toFixed(2)} SOL` : "—"}
                  </div>
                </div>
              </div>

              {/* Worst Trade */}
              <div className="bg-black/40 border border-white/5 rounded-xl p-2.5 flex items-center gap-2.5">
                <ArrowDownRight className="size-4 text-rose-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[8px] uppercase tracking-wider text-white/40 font-bold">Worst Trade</div>
                  <div className="text-xs font-mono font-bold text-rose-400 truncate">
                    {kpis.worstTrade ? `${kpis.worstTrade.profitSol.toFixed(2)} SOL` : "—"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Main Section: Positions on LEFT, Calendar on RIGHT ── */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
            
            {/* ── Left Column: Selected Day Positions (5 cols) ───────── */}
            <div className="lg:col-span-5 bg-neutral-900/90 border border-white/10 rounded-2xl p-4 sm:p-5 flex flex-col h-full min-h-[520px]">
              {selectedDayData ? (
                <>
                  {/* Day Header */}
                  <div className="pb-3 border-b border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="size-4 text-emerald-400" />
                        <h4 className="text-sm sm:text-base font-black text-white font-mono">
                          {selectedDayData.date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                        </h4>
                      </div>
                      <button
                        onClick={() => {
                          setActiveDayModalData(selectedDayData);
                          setIsDayModalOpen(true);
                        }}
                        className="text-[10px] font-mono text-emerald-400 hover:text-emerald-300 flex items-center gap-1 font-bold bg-emerald-500/10 hover:bg-emerald-500/20 px-2 py-1 rounded-lg border border-emerald-500/20 transition-colors"
                        title="Open full-screen deep-dive modal for this day"
                      >
                        Deep Dive <ArrowUpRight className="size-3" />
                      </button>
                    </div>

                    {/* Day KPI summary */}
                    <div className="flex flex-wrap items-center gap-2 text-xs font-mono mt-2">
                      <span className={`font-black ${selectedDayData.pnlSol >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                        {selectedDayData.pnlSol >= 0 ? "+" : ""}{currencyMode === "SOL" 
                          ? `${selectedDayData.pnlSol.toFixed(2)} SOL` 
                          : `$${selectedDayData.pnlUsd.toFixed(0)}`}
                      </span>
                      <span className="text-white/20">•</span>
                      <span className="text-white/70">
                        {selectedDayData.tradesCount} Trades
                      </span>
                      <span className="text-white/20">•</span>
                      <span className="text-emerald-400/90 font-bold">
                        {((selectedDayData.winCount / selectedDayData.tradesCount) * 100).toFixed(0)}% Win
                      </span>
                      {selectedDayData.depositsSol > 0 && (
                        <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                          +{selectedDayData.depositsSol.toFixed(2)} SOL Dep
                        </span>
                      )}
                    </div>

                    {/* Search & Filter pills */}
                    <div className="flex flex-col gap-2 mt-3">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-white/30" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search positions by token or mint..."
                          className="w-full pl-8 pr-3 py-1.5 text-[11px] font-mono bg-black/50 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-emerald-500/40"
                        />
                      </div>

                      <div className="flex items-center gap-1 bg-black/40 border border-white/10 p-1 rounded-lg text-[10px] font-mono">
                        <button
                          onClick={() => setFilterMode("ALL")}
                          className={`flex-1 py-1 rounded transition-colors text-center ${filterMode === "ALL" ? "bg-white/20 text-white font-bold" : "text-white/50 hover:text-white"}`}
                        >
                          All ({selectedDayData.tradesCount})
                        </button>
                        <button
                          onClick={() => setFilterMode("WINS")}
                          className={`flex-1 py-1 rounded transition-colors text-center ${filterMode === "WINS" ? "bg-emerald-500/20 text-emerald-400 font-bold border border-emerald-500/30" : "text-white/50 hover:text-white"}`}
                        >
                          Wins ({selectedDayData.winCount})
                        </button>
                        <button
                          onClick={() => setFilterMode("LOSSES")}
                          className={`flex-1 py-1 rounded transition-colors text-center ${filterMode === "LOSSES" ? "bg-rose-500/20 text-rose-400 font-bold border border-rose-500/30" : "text-white/50 hover:text-white"}`}
                        >
                          Losses ({selectedDayData.lossCount})
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Positions Scroll List */}
                  <div className="mt-2 divide-y divide-white/5 max-h-[500px] overflow-y-auto custom-scrollbar pr-1">
                    {filteredSelectedDayTrades.length === 0 ? (
                      <div className="text-center py-12 text-xs text-white/40 font-mono">
                        No positions match filter.
                      </div>
                    ) : (
                      filteredSelectedDayTrades.map((t, idx) => {
                        const isProfit = t.profitSol > 0;
                        const meta = t.tokenMeta || {};
                        return (
                          <div key={idx} className="py-2.5 px-2 hover:bg-white/[0.03] rounded-lg transition-colors flex items-center justify-between gap-2.5 text-[11px] font-mono group">
                            {/* Token info */}
                            <div className="flex items-center gap-2 min-w-0">
                              <TokenAvatar 
                                mint={t.mint} 
                                symbol={meta.symbol} 
                                name={meta.name} 
                                imageUri={meta.imageUri || meta.image} 
                                size="size-7" 
                              />
                              <div className="flex flex-col min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-bold text-white text-xs truncate max-w-[85px]" title={meta.symbol || truncate(t.mint, 6)}>
                                    {meta.symbol || truncate(t.mint, 4)}
                                  </span>
                                  {t.openReason && (
                                    <span className="text-[7.5px] px-1 py-0.2 rounded bg-white/5 text-emerald-400/80 border border-white/5 truncate max-w-[65px]">
                                      {t.openReason}
                                    </span>
                                  )}
                                </div>
                                <div className="text-[9px] text-white/40 flex items-center gap-1 mt-0.5">
                                  <span>{new Date(t.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                                  <span>•</span>
                                  <span>{formatDuration(t.duration)}</span>
                                </div>
                              </div>
                            </div>

                            {/* PnL and Chart Action */}
                            <div className="flex items-center gap-2.5 shrink-0 ml-auto text-right">
                              <div>
                                <div className={`font-black text-xs ${isProfit ? "text-emerald-400" : "text-rose-400"}`}>
                                  {isProfit ? "+" : ""}{currencyMode === "SOL" ? `${t.profitSol.toFixed(2)} SOL` : `$${t.pnlUsd.toFixed(0)}`}
                                </div>
                                <div className={`text-[9px] font-bold ${isProfit ? "text-emerald-400/70" : "text-rose-400/70"}`}>
                                  {isProfit ? "+" : ""}{(t.pnlPct * 100).toFixed(1)}%
                                </div>
                              </div>

                              {onSelectMint && (
                                <button
                                  onClick={() => {
                                    onSelectMint(t.mint);
                                    onClose();
                                  }}
                                  className="p-1 rounded-md bg-white/5 hover:bg-emerald-500/20 text-white/40 hover:text-emerald-400 border border-white/5 transition-colors"
                                  title="Open in Chart"
                                >
                                  <ExternalLink className="size-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-24 text-center my-auto">
                  <CalendarIcon className="size-8 text-white/20 mb-2" />
                  <p className="text-xs text-white/50 font-mono">Select a date on the calendar</p>
                  <p className="text-[10px] text-white/30 font-mono mt-1">Positions and trade logs will appear here</p>
                </div>
              )}
            </div>

            {/* ── Right Column: Monthly Heatmap Calendar (7 cols) ────── */}
            <div className="lg:col-span-7 bg-neutral-900/60 border border-white/10 rounded-2xl p-4 sm:p-5">
              {/* Single Day Cluster Notice & Spread Mode Toggle */}
              {isClusteredOnSingleDay && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3 py-2 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                    <span className="text-[10px] font-mono text-emerald-300">
                      {closedTrades.length} trades recorded in today's active session (Sep 19).
                    </span>
                  </div>
                  <div className="flex items-center gap-1 bg-black/40 border border-white/10 p-0.5 rounded-lg text-[9px] font-mono self-start sm:self-auto shrink-0">
                    <button
                      type="button"
                      onClick={() => setSpreadMode(false)}
                      className={`px-2 py-1 rounded transition-colors ${!spreadMode ? "bg-emerald-500 text-black font-bold" : "text-white/60 hover:text-white"}`}
                    >
                      Session View (Today)
                    </button>
                    <button
                      type="button"
                      onClick={() => setSpreadMode(true)}
                      className={`px-2 py-1 rounded transition-colors ${spreadMode ? "bg-emerald-500 text-black font-bold" : "text-white/60 hover:text-white"}`}
                      title="Distribute trades across the days of the month to preview multi-day journal"
                    >
                      Spread Across Month
                    </button>
                  </div>
                </div>
              )}

              {/* Calendar Controls */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h4 className="text-sm sm:text-base font-black tracking-tight text-white flex items-center gap-2">
                    <CalendarIcon className="size-4 text-emerald-400" />
                    {monthName}
                  </h4>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={prevMonth}
                      className="p-1 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors"
                      title="Previous month"
                    >
                      <ChevronLeft className="size-4" />
                    </button>
                    <button
                      onClick={nextMonth}
                      className="p-1 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors"
                      title="Next month"
                    >
                      <ChevronRight className="size-4" />
                    </button>
                  </div>
                </div>

                {/* Legend */}
                <div className="hidden sm:flex items-center gap-3 text-[10px] font-mono text-white/40">
                  <div className="flex items-center gap-1.5">
                    <div className="size-2.5 rounded bg-emerald-500/30 border border-emerald-500/50" />
                    <span>Profit Day</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="size-2.5 rounded bg-rose-500/25 border border-rose-500/40" />
                    <span>Loss Day</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="size-2.5 rounded bg-amber-500/30 border border-amber-500/50" />
                    <span>Deposit</span>
                  </div>
                </div>
              </div>

              {/* Days of week header */}
              <div className="grid grid-cols-7 gap-1.5 sm:gap-2 mb-1.5 text-center text-[10px] font-mono font-bold text-white/40 uppercase">
                <div>Sun</div>
                <div>Mon</div>
                <div>Tue</div>
                <div>Wed</div>
                <div>Thu</div>
                <div>Fri</div>
                <div>Sat</div>
              </div>

              {/* Calendar Days Grid */}
              <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
                {calendarDays.map((cell, idx) => {
                  const dayData = cell.data;
                  const hasTrades = dayData && dayData.tradesCount > 0;
                  const isProfit = hasTrades && dayData.pnlSol > 0;
                  const isLoss = hasTrades && dayData.pnlSol < 0;
                  const isSelected = selectedDayKey === cell.dateKey;
                  const hasDeposit = dayData && dayData.depositsSol > 0;

                  // Heatmap styling based on PnL intensity
                  let bgClasses = "bg-white/[0.02] border-white/5 text-white/30";
                  if (!cell.isCurrentMonth) {
                    bgClasses = "opacity-30 bg-transparent border-transparent text-white/20";
                  } else if (hasTrades) {
                    if (isProfit) {
                      if (dayData.pnlSol >= 10) {
                        bgClasses = "bg-emerald-500/25 border-emerald-500/50 text-emerald-300 shadow-[0_0_12px_rgba(16,185,129,0.15)]";
                      } else {
                        bgClasses = "bg-emerald-500/15 border-emerald-500/30 text-emerald-400";
                      }
                    } else if (isLoss) {
                      bgClasses = "bg-rose-500/20 border-rose-500/40 text-rose-300";
                    } else {
                      bgClasses = "bg-white/5 border-white/10 text-white/70";
                    }
                  }

                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setSelectedDayKey(cell.dateKey);
                        if (dayData) {
                          setActiveDayModalData(dayData);
                        } else {
                          const parts = cell.dateKey.split("-").map(Number);
                          setActiveDayModalData({
                            dateKey: cell.dateKey,
                            date: new Date(parts[0], parts[1] - 1, parts[2]),
                            pnlSol: 0,
                            pnlUsd: 0,
                            tradesCount: 0,
                            winCount: 0,
                            lossCount: 0,
                            trades: [],
                            depositsSol: 0,
                          });
                        }
                      }}
                      title={hasTrades ? `${dayData.tradesCount} trades · Click to view positions on the left` : `Select ${cell.dateKey}`}
                      className={`relative min-h-[64px] sm:min-h-[76px] p-2 rounded-xl border flex flex-col justify-between text-left transition-all duration-200 group cursor-pointer ${bgClasses} ${
                        isSelected ? "ring-2 ring-emerald-400 border-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.3)] z-10 scale-[1.02]" : "hover:border-white/20 hover:scale-[1.01]"
                      }`}
                    >
                      {/* Top line: Day Number + Badges */}
                      <div className="flex items-center justify-between w-full">
                        <span className={`text-[11px] font-mono font-bold ${cell.isCurrentMonth ? "text-white/90" : "text-white/30"}`}>
                          {cell.dayNumber}
                        </span>

                        <div className="flex items-center gap-1">
                          {hasDeposit && (
                            <span 
                              className="inline-flex items-center text-[7.5px] font-mono px-1 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40"
                              title={`Deposit: +${dayData.depositsSol.toFixed(2)} SOL`}
                            >
                              DEP
                            </span>
                          )}
                          {hasTrades && (
                            <span className="text-[7.5px] font-mono px-1 rounded bg-black/40 text-white/60">
                              {dayData.tradesCount}t
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Middle: Daily PnL */}
                      {hasTrades ? (
                        <div className="my-auto py-0.5">
                          <div className={`text-[11px] sm:text-xs font-mono font-black tracking-tight ${isProfit ? "text-emerald-400" : isLoss ? "text-rose-400" : "text-white/70"}`}>
                            {isProfit ? "+" : ""}{currencyMode === "SOL" 
                              ? `${dayData.pnlSol.toFixed(2)} SOL` 
                              : `$${dayData.pnlUsd.toFixed(0)}`}
                          </div>
                          <div className="text-[8.5px] font-mono text-white/50">
                            {dayData.winCount}W - {dayData.lossCount}L
                          </div>
                        </div>
                      ) : hasDeposit ? (
                        <div className="my-auto py-0.5">
                          <div className="text-[9px] font-mono font-bold text-amber-300">
                            +{dayData.depositsSol.toFixed(2)} SOL
                          </div>
                          <div className="text-[7.5px] font-mono text-white/40">Deposit</div>
                        </div>
                      ) : (
                        <div className="my-auto text-[8.5px] text-white/10 font-mono">No trades</div>
                      )}

                      {/* Bottom strip: mini win rate bar */}
                      {hasTrades && (
                        <div className="w-full h-1 bg-black/40 rounded-full overflow-hidden flex">
                          <div 
                            className="h-full bg-emerald-500" 
                            style={{ width: `${(dayData.winCount / dayData.tradesCount) * 100}%` }} 
                          />
                          <div 
                            className="h-full bg-rose-500" 
                            style={{ width: `${(dayData.lossCount / dayData.tradesCount) * 100}%` }} 
                          />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

          </div>

        </div>
      </div>

      {/* ── Day Detail Modal (opens on clicking any date on the calendar) ── */}
      <DayTradesDetailModal
        isOpen={isDayModalOpen}
        onClose={() => setIsDayModalOpen(false)}
        dayData={activeDayModalData}
        solPrice={solPrice}
        onSelectMint={(mint) => {
          onSelectMint?.(mint);
          onClose();
        }}
      />
    </div>
  );
}
